/**
 * Speech Recording Service
 * Handles Firebase Storage uploads and Firestore document management
 * Integrates with Firebase Functions trigger-based architecture
 */

import { ref, uploadBytes } from 'firebase/storage';
import { doc, setDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { storage, db } from './firebaseConfig';
import {
  RecordingDocument,
  ProcessedRecordingDocument,
  ErrorCode,
  RECORDING_CONFIG,
} from '../types/speechRecording';

/**
 * Generates a unique recording ID
 */
export const generateRecordingId = (): string => {
  return uuidv4();
};

/**
 * Gets the optimal MIME type for the current browser
 * Handles browser-specific preferences and capabilities
 */
export const getSupportedMimeType = (): string => {
  if (typeof MediaRecorder === 'undefined') {
    return 'audio/wav';
  }

  // Check each MIME type in order of preference
  for (const mimeType of RECORDING_CONFIG.SUPPORTED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(mimeType)) {
      console.log(`[SpeechRecording] Using MIME type: ${mimeType}`);
      return mimeType;
    }
  }

  // Fallback to wav if nothing else is supported
  console.warn('[SpeechRecording] No supported MIME types found, falling back to audio/wav');
  return 'audio/wav';
};

/**
 * Checks if MediaRecorder is supported in the current browser
 */
export const isMediaRecorderSupported = (): boolean => {
  return (
    typeof MediaRecorder !== 'undefined' &&
    RECORDING_CONFIG.SUPPORTED_MIME_TYPES.some(type => MediaRecorder.isTypeSupported(type))
  );
};

/**
 * Gets detailed browser and MediaRecorder support information for debugging
 * Useful for troubleshooting cross-browser compatibility issues
 */
export const getBrowserSupportInfo = (): {
  userAgent: string;
  mediaRecorderSupported: boolean;
  supportedMimeTypes: string[];
  selectedMimeType: string;
  browserName: string;
} => {
  const supportedMimeTypes = RECORDING_CONFIG.SUPPORTED_MIME_TYPES.filter(
    type => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type)
  );

  // Detect browser name for debugging
  const userAgent = navigator.userAgent.toLowerCase();
  let browserName = 'unknown';
  if (userAgent.includes('chrome') && !userAgent.includes('edge')) {
    browserName = 'chrome';
  } else if (userAgent.includes('firefox')) {
    browserName = 'firefox';
  } else if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
    browserName = 'safari';
  } else if (userAgent.includes('edge')) {
    browserName = 'edge';
  }

  return {
    userAgent: navigator.userAgent,
    mediaRecorderSupported: isMediaRecorderSupported(),
    supportedMimeTypes,
    selectedMimeType: getSupportedMimeType(),
    browserName,
  };
};

/**
 * Gets browser metadata for recording document
 */
const getBrowserMetadata = (mediaRecorder: MediaRecorder): RecordingDocument['metadata'] => {
  let sampleRate: number | undefined;

  try {
    // Try to get sample rate from AudioContext if available
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    sampleRate = audioContext.sampleRate;
    audioContext.close(); // Clean up
  } catch (error: unknown) {
    // AudioContext not available or failed, that's okay
    console.debug('Could not get AudioContext sample rate:', error);
  }

  return {
    browser: navigator.userAgent,
    mimeType: mediaRecorder.mimeType,
    sampleRate,
  };
};

/**
 * Gets the appropriate file extension based on MIME type
 */
const getFileExtensionFromMimeType = (mimeType: string): string => {
  // Handle specific MIME types
  if (mimeType.includes('webm')) {
    return 'webm';
  } else if (mimeType.includes('mp4')) {
    return 'mp4';
  } else if (mimeType.includes('wav')) {
    return 'wav';
  } else if (mimeType.includes('mp3')) {
    return 'mp3';
  } else if (mimeType.includes('m4a')) {
    return 'm4a';
  } else if (mimeType.includes('ogg')) {
    return 'ogg';
  } else if (mimeType.includes('flac')) {
    return 'flac';
  }

  // Default fallback based on browser detection
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
    // Safari typically produces MP4
    return 'mp4';
  } else if (userAgent.includes('chrome') || userAgent.includes('firefox')) {
    // Chrome/Firefox typically produce WebM
    return 'webm';
  }

  // Ultimate fallback
  return 'wav';
};

/**
 * Uploads audio blob to Firebase Storage
 * Path: /audio-recordings/{userId}/{recordingId}.{extension}
 */
export const uploadAudioToStorage = async (
  userId: string,
  recordingId: string,
  audioBlob: Blob
): Promise<string> => {
  try {
    // Debug: Check authentication state
    const { auth } = await import('./firebaseConfig');
    const currentUser = auth.currentUser;
    console.log(`[SpeechRecording] Auth state before upload:`, {
      isAuthenticated: !!currentUser,
      userId: currentUser?.uid,
      providedUserId: userId,
      userIdMatch: currentUser?.uid === userId,
    });

    // Determine file extension based on MIME type
    const extension = getFileExtensionFromMimeType(audioBlob.type);
    const storagePath = `audio-recordings/${userId}/${recordingId}.${extension}`;
    const storageRef = ref(storage, storagePath);

    console.log(`[SpeechRecording] Uploading audio:`, {
      path: storagePath,
      mimeType: audioBlob.type,
      size: audioBlob.size,
      extension,
      storageBucket: storage.app.options.storageBucket,
    });

    await uploadBytes(storageRef, audioBlob);

    console.log(`[SpeechRecording] Upload successful: ${storagePath}`);
    return storagePath;
  } catch (error) {
    console.error('[SpeechRecording] Upload failed:', error);
    if (error && typeof error === 'object') {
      console.error('[SpeechRecording] Error details:', {
        message: (error as any).message,
        code: (error as any).code,
        serverResponse: (error as any).serverResponse,
      });
    }
    throw error;
  }
};

/**
 * Creates initial Firestore recording document
 * Path: /users/{userId}/recordings/{recordingId}
 */
export const createRecordingDocument = async (
  userId: string,
  recordingId: string,
  storagePath: string,
  mediaRecorder: MediaRecorder
): Promise<void> => {
  try {
    const recordingDoc: RecordingDocument = {
      id: recordingId,
      userId,
      status: 'processing',
      createdAt: serverTimestamp() as any,
      storage_path: storagePath,
      metadata: getBrowserMetadata(mediaRecorder),
    };

    const docRef = doc(db, `users/${userId}/recordings/${recordingId}`);
    await setDoc(docRef, recordingDoc);

    console.log(`[SpeechRecording] Created recording document: ${recordingId}`);
  } catch (error) {
    console.error('[SpeechRecording] Failed to create recording document:', error);
    throw error;
  }
};

/**
 * Sets up real-time listener for recording document updates
 * Returns unsubscribe function
 */
export const listenToRecordingDocument = (
  userId: string,
  recordingId: string,
  callback: (data: ProcessedRecordingDocument | null) => void
): (() => void) => {
  const docRef = doc(db, `users/${userId}/recordings/${recordingId}`);

  console.log(`[SpeechRecording] Setting up listener for: ${recordingId}`);

  const unsubscribe = onSnapshot(
    docRef,
    doc => {
      if (doc.exists()) {
        const data = doc.data() as ProcessedRecordingDocument;
        console.log(`[SpeechRecording] Document updated:`, {
          recordingId,
          status: data.status,
          hasTranscribedText: !!data.transcribedText,
        });
        callback(data);
      } else {
        console.warn(`[SpeechRecording] Document not found: ${recordingId}`);
        callback(null);
      }
    },
    error => {
      console.error(`[SpeechRecording] Listener error for ${recordingId}:`, error);
      callback(null);
    }
  );

  return unsubscribe;
};

/**
 * Maps Firebase Functions error messages to translation keys
 * Based on the error patterns from the Firebase Functions backend
 * Returns translation keys that should be used with the i18n translation function
 */
export const getErrorTranslationKey = (error: unknown): string => {
  const errorMessage =
    error && typeof error === 'object' && 'message' in error ? String(error.message) : '';

  // Handle Firebase Functions error messages (as documented)
  if (errorMessage.includes('No speech content')) {
    return 'speechRecording.noSpeechDetected';
  }
  if (errorMessage.includes('too large') || errorMessage.includes('25MB')) {
    return 'speechRecording.fileTooLarge';
  }
  if (errorMessage.includes('temporarily unavailable') || errorMessage.includes('high demand')) {
    return 'speechRecording.serviceBusy';
  }
  if (errorMessage.includes('not supported') && errorMessage.includes('format')) {
    return 'speechRecording.unsupportedFormat';
  }
  if (errorMessage.includes('too long') && errorMessage.includes('cancelled')) {
    return 'speechRecording.processingTimeout';
  }
  if (errorMessage.includes('not found')) {
    return 'speechRecording.uploadFailed';
  }
  if (errorMessage.includes('Recording stopped automatically')) {
    return 'speechRecording.recordingTimeout';
  }

  // Handle frontend-specific errors
  if (errorMessage.includes('upload')) {
    return 'speechRecording.uploadFailed';
  }
  if (errorMessage.includes('permission')) {
    return 'speechRecording.permissionDenied';
  }
  if (errorMessage.includes('not supported')) {
    return 'speechRecording.notSupported';
  }

  // Default fallback
  return 'speechRecording.error';
};

/**
 * Detects if the current device is likely a mobile device
 * Used for applying mobile-specific MediaRecorder timing adjustments
 */
export const isMobileDevice = (): boolean => {
  return (
    /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    window.innerWidth <= 768
  );
};

/**
 * Stops MediaRecorder and waits for all data to be available
 * Handles cross-browser differences in MediaRecorder stopping behavior
 * Uses event-driven approach with fallback timeout for reliability
 */
export const stopRecordingAndWaitForData = (
  mediaRecorder: MediaRecorder,
  audioChunks: Blob[]
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    let isResolved = false;

    // Mobile devices (especially iPhone Safari) need more time for final chunks
    const timeoutMs = isMobileDevice() ? 1000 : 300;

    // Safety timeout to prevent hanging
    const timeout = setTimeout(() => {
      if (!isResolved) {
        isResolved = true;
        console.warn(
          '[SpeechRecording] Timeout waiting for MediaRecorder stop, creating blob with available chunks'
        );
        resolve(new Blob(audioChunks, { type: mediaRecorder.mimeType }));
      }
    }, timeoutMs);

    // Event-driven approach - wait for actual stop event
    const originalOnStop = mediaRecorder.onstop;
    mediaRecorder.onstop = event => {
      // Call original handler if it existed
      if (originalOnStop) {
        originalOnStop.call(mediaRecorder, event);
      }

      clearTimeout(timeout);
      if (!isResolved) {
        isResolved = true;
        // Small buffer to ensure any final ondataavailable events are processed
        setTimeout(() => {
          resolve(new Blob(audioChunks, { type: mediaRecorder.mimeType }));
        }, 50);
      }
    };

    // Actually stop the recorder
    try {
      console.log('[SpeechRecording] Stopping MediaRecorder, waiting for completion...');
      mediaRecorder.stop();

      // Stop stream tracks after a small delay to avoid interfering with final chunk delivery
      setTimeout(() => {
        if (mediaRecorder.stream) {
          mediaRecorder.stream.getTracks().forEach(track => track.stop());
        }
      }, 100);
    } catch (error) {
      clearTimeout(timeout);
      if (!isResolved) {
        isResolved = true;
        reject(error);
      }
    }
  });
};

/**
 * Validates audio blob before upload
 * Returns translation keys for error messages
 */
export const validateAudioBlob = (audioBlob: Blob): { isValid: boolean; errorKey?: string } => {
  // Check file size (25MB limit)
  const maxSizeBytes = RECORDING_CONFIG.MAX_FILE_SIZE_MB * 1024 * 1024;
  if (audioBlob.size > maxSizeBytes) {
    return {
      isValid: false,
      errorKey: 'speechRecording.fileTooLarge',
    };
  }

  // Check if blob is empty
  if (audioBlob.size === 0) {
    return {
      isValid: false,
      errorKey: 'speechRecording.error',
    };
  }

  return { isValid: true };
};
